import { buildCurrencyOptions, isValidEmail } from '@choco-admin/utils';
import React, { useMemo } from 'react';

import {
  ChocoCheckbox,
  ChocoSelect,
  Input,
  LocaleSelectField,
  TextArea,
} from '../../../../../components/FormComponents';
import { Pill } from '../../../../../components/Pill';
import { useSupplierAssortmentsDataQuery } from '../../../../../generated';
import { parseEmailList } from '../../../utils/buildIntegrationPayload';
import { useSupplierForm } from '../../SupplierForm/SupplierFormProvider';
import { TimezoneField } from '../../SupplierForm/tabs/SettingsTab/components';

import { CreateFormSection } from './CreateFormSection';

/**
 * Everything a distributor needs before it exists, on one page.
 *
 * The order is deliberate: what it is, then how orders reach it, then the
 * regional settings that decide how those orders read, then the commercial terms
 * that can wait. The first three groups are the ones that leave a distributor
 * broken if they are wrong, so they come first and they are the ones marked
 * required.
 *
 * Everything else the edit form offers stays on the edit form. A field that
 * cannot be set here still reaches the backend, because `buildSupplierUpdate`
 * and `useCreateSupplier` read the whole `SupplierFormData` and the provider
 * defaults it: this component decides what is worth asking on day one, not what
 * the payload carries.
 */
export const CreateFormFields: React.FC = () => {
  const { data: assortmentsData, loading: assortmentsLoading } =
    useSupplierAssortmentsDataQuery();

  const {
    formMethods: {
      watch,
      setValue,
      formState: { errors },
    },
  } = useSupplierForm();

  const secondaryCategories = watch('secondaryCategories') ?? [];
  const assortments = assortmentsData?.supplierAssortments.assortments ?? [];

  const toggleCategory = (category: string) => {
    setValue(
      'secondaryCategories',
      secondaryCategories.includes(category)
        ? secondaryCategories.filter((c: string) => c !== category)
        : [...secondaryCategories, category],
      { shouldDirty: true }
    );
  };

  // Attachments need a recipient that can receive them, the same rule the
  // Notifications tab applies.
  const currencyOptions = useMemo(() => buildCurrencyOptions(), []);

  const orderRecipients = parseEmailList(watch('notificationOrderEmails'));
  const canAttach =
    orderRecipients.length > 0 && orderRecipients.every(isValidEmail);

  return (
    <div className="divide-y divide-divider">
      <CreateFormSection
        title="The distributor"
        description="What buyers see, and how this distributor is found."
      >
        <Input
          id="create-name"
          label="Company name"
          value={watch('name')}
          onChange={(value) => setValue('name', value, { shouldDirty: true })}
          placeholder="Company name"
          errorMessage={errors.name?.message}
          required
        />

        <Input
          id="create-code"
          label="External code"
          value={watch('code')}
          onChange={(value) => setValue('code', value, { shouldDirty: true })}
          placeholder="Code from the vendor's own systems"
          helperText="The code the vendor's own systems, such as their ERP, use for this distributor."
          isInvalid={Boolean(errors.code)}
          errorMessage={errors.code?.message}
        />

        <Input
          id="create-externalName"
          label="External name"
          value={watch('externalName')}
          onChange={(value) =>
            setValue('externalName', value, { shouldDirty: true })
          }
          placeholder="Name from the vendor's own systems"
          helperText="The name the vendor's own systems, such as their ERP, use for this distributor."
          isInvalid={Boolean(errors.externalName)}
          errorMessage={errors.externalName?.message}
        />

        <ChocoSelect
          id="create-primary-category"
          label="Primary category"
          placeholder="Select primary category"
          options={assortments.map((category: string) => ({
            value: category,
            label: category,
          }))}
          value={watch('primaryCategory')}
          onSelectionChange={(value) =>
            setValue('primaryCategory', value ?? '', { shouldDirty: true })
          }
          isLoading={assortmentsLoading}
          errorMessage={errors.primaryCategory?.message}
          isRequired
        />

        <TextArea
          id="create-description"
          label="Company description"
          value={watch('description')}
          onChange={(value) =>
            setValue('description', value, { shouldDirty: true })
          }
          placeholder="A line or two about the distributor"
          rows={2}
          errorMessage={errors.description?.message}
        />

        <div>
          <p className="mb-2 text-small font-medium text-foreground">
            Secondary categories
          </p>
          <div className="flex flex-wrap gap-2">
            {assortments.map((category: string) => (
              <Pill
                key={category}
                variant={
                  secondaryCategories.includes(category) ? 'primary' : 'default'
                }
                className="cursor-pointer"
                onClick={() => toggleCategory(category)}
              >
                {category}
              </Pill>
            ))}
          </div>
          <p className="mt-2 text-tiny text-default-500">
            Select as many as you like.
          </p>
        </div>
      </CreateFormSection>

      <CreateFormSection
        title="Where orders go"
        description="A central address for this distributor's orders. Leave it blank if their buyers order through individual sales reps instead: those orders follow each rep's own notification settings."
      >
        <Input
          id="create-order-emails"
          label="Order emails"
          value={watch('notificationOrderEmails')}
          onChange={(value) =>
            setValue('notificationOrderEmails', value, { shouldDirty: true })
          }
          placeholder="orders@distributor.com, ops@distributor.com"
          errorMessage={errors.notificationOrderEmails?.message}
          isInvalid={!!errors.notificationOrderEmails}
        />

        <div className="space-y-3">
          <p className="text-small font-medium text-foreground">
            Attach a copy of the order
          </p>
          <ChocoCheckbox
            isSelected={watch('notificationOrderPdf')}
            onValueChange={(isSelected: boolean) =>
              setValue('notificationOrderPdf', isSelected, {
                shouldDirty: true,
              })
            }
            isDisabled={!canAttach}
            label="PDF"
            description={
              canAttach
                ? 'Attach a PDF of the order to those emails.'
                : 'Needs a valid order email above.'
            }
          />
          <ChocoCheckbox
            isSelected={watch('notificationOrderXlsx')}
            onValueChange={(isSelected: boolean) =>
              setValue('notificationOrderXlsx', isSelected, {
                shouldDirty: true,
              })
            }
            isDisabled={!canAttach}
            label="Excel"
            description={
              canAttach
                ? 'Attach an Excel copy of the order to those emails.'
                : 'Needs a valid order email above.'
            }
          />
        </div>
      </CreateFormSection>

      <CreateFormSection
        title="Language and time zone"
        description="These decide how an order reads and when it is due, so they are set before the first order rather than after."
      >
        <LocaleSelectField
          value={watch('locale')}
          onChange={(value) => setValue('locale', value, { shouldDirty: true })}
          isRequired
          errorMessage={errors.locale?.message}
          data-testid="create-locale-field"
          helperText="Sets the distributor's default currency and date formats."
        />

        <TimezoneField />
      </CreateFormSection>

      <CreateFormSection
        title="Commercial terms"
        description="All optional, and all editable later on the distributor's Profile tab."
      >
        <Input
          id="create-delivery-fee"
          label="Delivery fee"
          value={watch('deliveryFee')}
          onChange={(value) =>
            setValue('deliveryFee', value.replace(/[^0-9.]/g, ''), {
              shouldDirty: true,
            })
          }
          placeholder="Enter delivery fee"
          errorMessage={errors.deliveryFee?.message}
        />

        <div className="flex gap-4">
          <Input
            id="create-minimum-order"
            label="Minimum order value"
            value={watch('minimumOrderValue')}
            onChange={(value) =>
              setValue('minimumOrderValue', value.replace(/[^0-9.]/g, ''), {
                shouldDirty: true,
              })
            }
            placeholder="Enter minimum order value"
            className="flex-grow"
            errorMessage={errors.minimumOrderValue?.message}
          />
          <ChocoSelect
            id="create-minimum-order-currency"
            label="Currency"
            placeholder="Select currency"
            options={currencyOptions}
            value={watch('minimumOrderCurrency')}
            onSelectionChange={(value) =>
              setValue('minimumOrderCurrency', value ?? '', {
                shouldDirty: true,
              })
            }
            className="w-56"
            errorMessage={errors.minimumOrderCurrency?.message}
          />
        </div>

        <ChocoCheckbox
          isSelected={watch('minOrderStrict')}
          onValueChange={(isSelected: boolean) =>
            setValue('minOrderStrict', isSelected, { shouldDirty: true })
          }
          label="Strict minimum order (eCommerce)"
          description="Block an eCommerce checkout below the minimum. Without this the minimum is advisory."
        />
      </CreateFormSection>
    </div>
  );
};
